import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { format } from 'date-fns'
import { useInRouterContext } from 'react-router-dom'

import DownloadIcon from '@mui/icons-material/Download'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import axios from '../../dataClient/dataClient'
import { handleDownloadResponse } from '../../util/downloadFile'
import { SecondaryIconButton } from '../common/Button'
import { BoldMedBodyText, MedBodyText, MiniErrorText, SmallBodyText } from '../styles/Text'
import { MedBGContainer, FlexRow, FlexColumn, FlexEndContainer, FlexEndButtonErrorContainer } from '../styles/Containers'
import { theme } from '../../themes/theme'
import { MUTED_TEXT } from '../styles/colors'
import { redactionFileTypeMap, redactionFileTypes } from '../../util/categoryConsts'
import RenameFileButton from '../files/RenameFileButton'
import ReplaceRedactionFileButton from './ReplaceRedactionFileButton'
import OpenRedactionFileButton from './OpenRedactionFileButton'

const Row = styled(FlexRow)`
  align-items: center;
`

const FileInfo = styled(MedBGContainer)`
  width: 100%;
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 12px 20px;
  border: none;
  font-family: inherit;
  text-align: left;
  cursor: ${props => (props.as === 'button' ? 'pointer' : 'default')};

  &[data-active='true'],
  &:hover:not([data-static='true']) {
    outline: 1px solid ${theme.colors.primary};
    outline-offset: -1px;
  }

  .MuiSvgIcon-root,
  .MuiCircularProgress-root {
    height: 22px;
    width: 22px;
  }
`

const FileNameContainer = styled(FlexColumn)`
  flex: 1;
  gap: 2px;
  min-width: 0;
`

const Filename = styled(SmallBodyText)`
  color: ${MUTED_TEXT};
  word-break: break-word;
`

const DateTimeContainer = styled(FlexColumn)`
  gap: 5px;
  flex: 1;
`

const IconContainer = styled(FlexEndContainer)`
  width: 5%;
`

const CheckIcon = styled(CheckCircleIcon)`
  color: rgba(16, 192, 208, 1);
`

const ErrorText = styled(MiniErrorText)`
  text-align: right;
`

function RedactionFileRow({
  file,
  foiId,
  loading = false,
  viewOnly = false,
  active = false,
  onPreview = null,
  handleReplaceFile = () => {},
  handleRenameFile = () => {}
}) {
  const [downloadError, setDownloadError] = useState(false)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const showOpenAction = useInRouterContext() && Boolean(foiId)

  function handleDownload() {
    setDownloadError(false)
    setDownloadLoading(true)

    axios
      .get(`/file/${file.id}/download`, { responseType: 'blob' })
      .then(response => handleDownloadResponse(response, file.filename, 'download'))
      .catch(e => {
        setDownloadError(true)
        console.error('An error occurred while downloading the document', file.filename, e)
      })
      .finally(() => setDownloadLoading(false))
  }

  const previewable = Boolean(onPreview)

  return (
    <Row>
      <FileInfo
        as={previewable ? 'button' : undefined}
        type={previewable ? 'button' : undefined}
        onClick={previewable ? () => onPreview(file) : undefined}
        aria-label={previewable ? `Preview ${file.filename}` : undefined}
        data-static={previewable ? undefined : 'true'}
        data-active={active ? 'true' : undefined}
        data-testid="redaction-file-info">
        <FileNameContainer>
          <BoldMedBodyText>{redactionFileTypeMap[file.status]}</BoldMedBodyText>
          <Filename data-testid="redaction-file-name">{file.filename}</Filename>
        </FileNameContainer>
        <DateTimeContainer>
          <BoldMedBodyText>{format(file.lastModified, 'MMM-dd-yyyy')}</BoldMedBodyText>
          <MedBodyText>{format(file.lastModified, 'hh:mm a')}</MedBodyText>
        </DateTimeContainer>
        <IconContainer>{file.usage === 'RESPONSE' && <CheckIcon />}</IconContainer>
      </FileInfo>
      <FlexRow gap="5px">
        {showOpenAction && (
          <FlexEndButtonErrorContainer gap="0">
            <OpenRedactionFileButton file={file} foiId={foiId} disabled={loading} viewOnly={viewOnly} />
          </FlexEndButtonErrorContainer>
        )}
        {!viewOnly && (
          <FlexEndButtonErrorContainer>
            <RenameFileButton file={file} disabled={loading || downloadLoading} onSuccess={handleRenameFile} />
          </FlexEndButtonErrorContainer>
        )}
        <FlexEndButtonErrorContainer gap="0">
          <SecondaryIconButton onClick={handleDownload} aria-label="Download File" loading={downloadLoading}>
            <DownloadIcon />
          </SecondaryIconButton>
          {downloadError && <ErrorText role="alert">Error, try again.</ErrorText>}
        </FlexEndButtonErrorContainer>
        {!viewOnly && (
          <FlexEndButtonErrorContainer>
            <ReplaceRedactionFileButton
              existingFile={file}
              categoryId={file.associationId}
              disabled={loading || file.status === 'COMBINED' || downloadLoading}
              onSuccess={handleReplaceFile}
            />
          </FlexEndButtonErrorContainer>
        )}
      </FlexRow>
    </Row>
  )
}

RedactionFileRow.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
    usage: PropTypes.string.isRequired,
    associationId: PropTypes.string.isRequired,
    status: PropTypes.oneOf(redactionFileTypes).isRequired,
    lastModified: PropTypes.instanceOf(Date).isRequired
  }).isRequired,
  foiId: PropTypes.string,
  loading: PropTypes.bool,
  viewOnly: PropTypes.bool,
  active: PropTypes.bool,
  onPreview: PropTypes.func,
  handleReplaceFile: PropTypes.func,
  handleRenameFile: PropTypes.func
}

export default RedactionFileRow
